import { AnimatedFolderIcon } from '@affine/component';
import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import { UNCATEGORIZED_FOLDER_ID } from '@affine/core/modules/organize';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import { NavigationPanelDocNode } from '../doc';
import { NavigationPanelTreeNode } from '../../tree';
import type { NavigationPanelTreeNodeIcon } from '../../tree/node';

const UncategorizedFolderIcon: NavigationPanelTreeNodeIcon = ({
  collapsed,
  className,
  draggedOver,
  treeInstruction,
}) => (
  <AnimatedFolderIcon
    className={className}
    open={
      !collapsed || (!!draggedOver && treeInstruction?.type === 'make-child')
    }
  />
);

/**
 * Virtual navigation node for documents that are not linked from a local
 * organize folder. The grouping itself cannot receive a drop because it has
 * no persisted folder identity; child documents remain ordinary document
 * nodes with their normal operations.
 */
export const NavigationPanelUncategorizedFolderNode = ({
  docIds,
  parentPath,
}: {
  docIds: readonly string[];
  parentPath: string[];
}) => {
  const t = useI18n();
  const navigationPanelService = useService(NavigationPanelService);
  const path = useMemo(
    () => [...parentPath, `folder-${UNCATEGORIZED_FOLDER_ID}`],
    [parentPath]
  );
  const collapsed = useLiveData(navigationPanelService.collapsed$(path));
  const setCollapsed = useCallback(
    (value: boolean) => navigationPanelService.setCollapsed(path, value),
    [navigationPanelService, path]
  );

  return (
    <NavigationPanelTreeNode
      icon={UncategorizedFolderIcon}
      name={t['com.affine.appearanceSettings.uncategorizedFolder.name']()}
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      collapsible
      reorderable={false}
      canDrop={() => false}
      data-testid="navigation-panel-uncategorized-folder"
    >
      {docIds.map(docId => (
        <NavigationPanelDocNode
          key={docId}
          docId={docId}
          reorderable={false}
          parentPath={path}
        />
      ))}
    </NavigationPanelTreeNode>
  );
};
